import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Card, Modal, Input, message, Spin, Select, Space, Typography, Tooltip, ColorPicker, Slider } from 'antd';
import { Plus, Save, Settings, Layout as LayoutIcon, ZoomIn, ZoomOut, RotateCcw, Type, Copy, Check, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import Icon from '../../../shared/components/Icon';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import Sidebar from '../../../shared/components/layout/Sidebar';
import EnhancedVisualization from '../../../shared/components/visualization/EnhancedVisualization';
import SavedQueriesSidebar from '../../../shared/components/modals/SavedQueriesSidebar';

const { Title, Text } = Typography;
const { Option } = Select;

// Updated Design System styles
const styles = {
  primaryBackground: "#ffffff", // White
  secondaryBackground: "#DDE2EE", // Light blue-grey
  textPrimary: "#0f172a", // Black for important elements
  textSecondary: "#666666", // Grey for regular text
  footerBackground: "#000000", // Black background
  footerText: "#ffffff", // White text for footer
  borderColor: "#DDE2EE", // Light blue-grey for borders
  cardBg: "#ffffff", // White card background
  cardShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
  cardShadowHover: "0 4px 16px rgba(0, 0, 0, 0.1)",
  // Typography
  headingFont: "Raleway, sans-serif",
  bodyFont: "Raleway, sans-serif"
};

const brandColor = '#1d0c46';

// Add CSS for infinite canvas widgets
const canvasStyles = `
  .canvas-widget {
    position: absolute;
    cursor: move;
    transition: box-shadow 0.2s ease;
    user-select: none;
  }
  .canvas-widget:hover {
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  }
  .canvas-widget.dragging {
    z-index: 1000;
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.25);
  }
  /* Remove hover effects in view mode */
  .view-mode .canvas-widget {
    transition: none;
    cursor: default;
  }
  .view-mode .canvas-widget:hover {
    box-shadow: none !important;
  }
  /* Remove Ant Design Card hover effects in view mode */
  .view-mode .ant-card {
    border-radius: 0 !important;
    transition: none !important;
  }
  .view-mode .ant-card:hover {
    box-shadow: none !important;
    transform: none !important;
  }
  .canvas-background {
    cursor: grab;
  }
  .canvas-background:active {
    cursor: grabbing;
  }
  .view-mode .canvas-background {
    cursor: default;
    border: none !important;
    border-width: 0 !important;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = canvasStyles;
  document.head.appendChild(styleElement);
}

// AddTextModal Component
const AddTextModal = ({ open, onCancel, onAdd, initialValues, isEdit = false }) => {
  const [text, setText] = useState('');
  const [fontSize, setFontSize] = useState(16);
  const [color, setColor] = useState('#1d0c46');
  const [fontFamily, setFontFamily] = useState('Inter, sans-serif');
  const [fontWeight, setFontWeight] = useState('normal');
  const [textAlign, setTextAlign] = useState('left');
  const textEditorRef = useRef(null);
  const isTypingRef = useRef(false);

  // Update form when initialValues change (for edit mode)
  useEffect(() => {
    if (initialValues && open) {
      setText(initialValues.text || '');
      setFontSize(initialValues.fontSize || 16);
      setColor(initialValues.color || '#1d0c46');
      setFontFamily(initialValues.fontFamily || 'Inter, sans-serif');
      setFontWeight(initialValues.fontWeight || 'normal');
      setTextAlign(initialValues.textAlign || 'left');
    } else if (open && !isEdit) {
      // Reset form for new text widget
      setText('');
      setFontSize(16);
      setColor('#1d0c46');
      setFontFamily('Inter, sans-serif');
      setFontWeight('normal');
      setTextAlign('left');
    }
  }, [initialValues, open, isEdit]);

  // Update contentEditable content when styles change (but not during typing)
  useEffect(() => {
    if (textEditorRef.current && open && !isTypingRef.current) {
      const editor = textEditorRef.current;
      editor.innerHTML = text || '<span style="color: #666666;">Enter your text here...</span>';
    }
  }, [fontSize, color, fontFamily, fontWeight, textAlign, open]);

  // Update content when text changes from external source (initial load or edit)
  useEffect(() => {
    if (textEditorRef.current && open && !isTypingRef.current) {
      const editor = textEditorRef.current;
      const currentText = editor.textContent || '';
      if (currentText !== text) {
        editor.innerHTML = text || '<span style="color: #666666;">Enter your text here...</span>';
      }
    }
  }, [text, open]);

  const fontFamilies = [
    { value: 'Inter, sans-serif', label: 'Inter' },
    { value: 'Arial, sans-serif', label: 'Arial' },
    { value: 'Helvetica, sans-serif', label: 'Helvetica' },
    { value: 'Georgia, serif', label: 'Georgia' },
    { value: 'Times New Roman, serif', label: 'Times New Roman' },
    { value: 'Courier New, monospace', label: 'Courier New' },
    { value: 'Raleway, sans-serif', label: 'Raleway' },
    { value: 'Raleway, sans-serif', label: 'Raleway' }
  ];

  const fontWeights = [
    { value: 'normal', label: 'Normal', weight: 400 },
    { value: '300', label: 'Light', weight: 300 },
    { value: '400', label: 'Regular', weight: 400 },
    { value: '500', label: 'Medium', weight: 500 },
    { value: '600', label: 'Semi Bold', weight: 600 },
    { value: '700', label: 'Bold', weight: 700 },
    { value: '800', label: 'Extra Bold', weight: 800 },
    { value: 'bold', label: 'Bold', weight: 700 }
  ];

  // Convert font weight string to numeric value for slider
  const getWeightValue = (weight) => {
    if (weight === 'normal') return 400;
    if (weight === 'bold') return 700;
    return parseInt(weight) || 400;
  };

  // Convert numeric value to font weight string
  const getWeightString = (value) => {
    if (value === 400) return 'normal';
    if (value === 700) return 'bold';
    return value.toString();
  };

  const textAligns = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' }
  ];

  const headingPresets = [
    { 
      name: 'Heading 1', 
      fontSize: 32, 
      fontWeight: 'bold', 
      color: '#1d0c46' 
    },
    { 
      name: 'Heading 2', 
      fontSize: 24, 
      fontWeight: '600', 
      color: '#1d0c46' 
    },
    { 
      name: 'Heading 3', 
      fontSize: 20, 
      fontWeight: '600', 
      color: '#1d0c46' 
    },
    { 
      name: 'Subheading', 
      fontSize: 18, 
      fontWeight: 'normal', 
      color: '#1d0c46' 
    },
    { 
      name: 'Normal Text', 
      fontSize: 16, 
      fontWeight: 'normal', 
      color: '#1d0c46'
    },
    { 
      name: 'Small Text', 
      fontSize: 14, 
      fontWeight: 'normal', 
      color: '#1d0c46' 
    }
  ];

  const handleAdd = () => {
    if (!text.trim()) {
      message.error('Please enter some text');
      return;
    }

    onAdd({
      text: text.trim(),
      fontSize,
      color,
      fontFamily,
      fontWeight,
      textAlign
    });

    // Reset form
    setText('');
    setFontSize(16);
    setColor('#1d0c46');
    setFontFamily('Inter, sans-serif');
    setFontWeight('normal');
    setTextAlign('left');
  };

  const applyPreset = (preset) => {
    setFontSize(preset.fontSize);
    setFontWeight(preset.fontWeight);
    setColor(preset.color);
  };

  // Check if a preset matches current settings
  const isPresetActive = (preset) => {
    return fontSize === preset.fontSize && 
           fontWeight === preset.fontWeight && 
           color === preset.color;
  };

  return (
    <Modal
      title={
        <span style={{ 
          color: '#1d0c46',
          fontFamily: styles.headingFont,
          fontWeight: 600
        }}>
          {isEdit ? "Edit Text" : "Add Text"}
        </span>
      }
      open={open}
      onCancel={onCancel}
      onOk={handleAdd}
      okText={isEdit ? "Update Text" : "Add Text"}
      width={500}
      style={{ top: 20 }}
      styles={{
        content: {
          borderRadius: 0,
          padding: 0
        },
        header: {
          borderRadius: 0,
          borderBottom: '1px solid #E5E7EB',
          padding: '16px 24px',
          margin: 0
        },
        body: {
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          padding: '20px',
          backgroundColor: styles.primaryBackground,
          borderRadius: 0
        },
        footer: {
          borderRadius: 0,
          borderTop: '1px solid #E5E7EB',
          padding: '12px 24px'
        }
      }}
      okButtonProps={{
        style: {
          backgroundColor: '#1d0c46',
          borderColor: '#1d0c46',
          fontFamily: 'Raleway, sans-serif',
          fontWeight: 500,
          borderRadius: '0px',
          height: '32px'
        }
      }}
      cancelButtonProps={{
        style: {
          color: '#1d0c46',
          borderColor: '#1d0c46',
          backgroundColor: 'transparent',
          fontFamily: 'Raleway, sans-serif',
          fontWeight: 500,
          borderRadius: '0px',
          height: '32px'
        }
      }}
    >
      <div className="space-y-4">
        {/* Text Input */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ 
            color: styles.textPrimary,
            fontFamily: styles.headingFont,
            fontWeight: 500
          }}>
            Text Content
          </label>
          <div
            ref={textEditorRef}
            style={{
              border: `1px solid ${styles.borderColor}`,
              borderRadius: 0,
              minHeight: '120px',
              padding: '12px',
              backgroundColor: styles.primaryBackground,
              fontSize: `${fontSize}px`,
              color: color,
              fontFamily: fontFamily,
              fontWeight: fontWeight,
              textAlign: textAlign,
              lineHeight: 1.2,
              overflow: 'auto',
              maxHeight: '300px',
              outline: 'none',
              cursor: 'text'
            }}
            contentEditable
            suppressContentEditableWarning
            onInput={(e) => {
              isTypingRef.current = true;
              const content = e.currentTarget.textContent || '';
              setText(content);
              setTimeout(() => {
                isTypingRef.current = false;
              }, 100);
            }}
            onBlur={(e) => {
              isTypingRef.current = false;
              const content = e.currentTarget.textContent || '';
              setText(content);
            }}
            onFocus={() => {
              isTypingRef.current = true;
            }}
          />
        </div>

        {/* Preset Headings */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ 
            color: styles.textPrimary,
            fontFamily: styles.headingFont,
            fontWeight: 500
          }}>
            Quick Presets
          </label>
          <div className="grid grid-cols-3 gap-2">
            {headingPresets.map((preset, index) => {
              const isActive = isPresetActive(preset);
              return (
                <Button
                  key={index}
                  size="small"
                  onClick={() => applyPreset(preset)}
                  style={{ 
                    fontSize: '11px',
                    fontFamily: styles.bodyFont,
                    justifyContent: 'center',
                    borderColor: isActive ? brandColor : styles.borderColor,
                    color: isActive ? brandColor : styles.textSecondary,
                    backgroundColor: 'transparent',
                    borderRadius: 0,
                    height: '28px'
                  }}
                >
                  {preset.name}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Font Settings */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ 
              color: styles.textPrimary,
              fontFamily: styles.headingFont,
              fontWeight: 500
            }}>
              Font Family
            </label>
            <Select
              value={fontFamily}
              onChange={setFontFamily}
              style={{ 
                width: '100%',
                fontFamily: styles.bodyFont
              }}
            >
              {fontFamilies.map(font => (
                <Option key={font.value} value={font.value}>
                  <span style={{ fontFamily: font.value }}>{font.label}</span>
                </Option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ 
              color: styles.textPrimary,
              fontFamily: styles.headingFont,
              fontWeight: 500
            }}>
              Text Color
            </label>
            <ColorPicker
              value={color}
              onChange={(color) => setColor(color.toHexString())}
              showText
              style={{ 
                width: '100%',
                fontFamily: styles.bodyFont
              }}
            />
          </div>
        </div>

        {/* Size and Weight */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium" style={{ 
                color: styles.textPrimary,
                fontFamily: styles.headingFont,
                fontWeight: 500
              }}>
                Font Size
              </label>
              <span style={{ 
                color: brandColor,
                fontFamily: styles.bodyFont,
                fontSize: '13px',
                fontWeight: 500
              }}>
                {fontSize}px
              </span>
            </div>
            <div style={{ padding: '0 4px' }}>
              <Slider
                min={8}
                max={72}
                step={1}
                value={fontSize}
                onChange={setFontSize}
                tooltip={{
                  formatter: (value) => `${value}px`
                }}
                styles={{
                  track: {
                    backgroundColor: brandColor
                  },
                  handle: {
                    borderColor: brandColor
                  },
                  rail: {
                    backgroundColor: styles.borderColor
                  }
                }}
                style={{ 
                  fontFamily: styles.bodyFont
                }}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium" style={{ 
                color: styles.textPrimary,
                fontFamily: styles.headingFont,
                fontWeight: 500
              }}>
                Font Weight
              </label>
              <span style={{ 
                color: brandColor,
                fontFamily: styles.bodyFont,
                fontSize: '13px',
                fontWeight: 500
              }}>
                {fontWeights.find(w => w.value === fontWeight || w.weight === getWeightValue(fontWeight))?.label || 'Normal'}
              </span>
            </div>
            <div style={{ padding: '0 4px' }}>
              <Slider
                min={300}
                max={800}
                step={100}
                value={getWeightValue(fontWeight)}
                onChange={(value) => setFontWeight(getWeightString(value))}
                tooltip={{
                  formatter: (value) => {
                    const weight = fontWeights.find(w => w.weight === value);
                    return weight ? weight.label : value;
                  }
                }}
                styles={{
                  track: {
                    backgroundColor: brandColor
                  },
                  handle: {
                    borderColor: brandColor
                  },
                  rail: {
                    backgroundColor: styles.borderColor
                  }
                }}
                style={{ 
                  fontFamily: styles.bodyFont
                }}
              />
            </div>
          </div>
        </div>

        {/* Text Alignment */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ 
            color: styles.textPrimary,
            fontFamily: styles.headingFont,
            fontWeight: 500
          }}>
            Text Alignment
          </label>
          <div className="flex gap-2">
            <Button
              onClick={() => setTextAlign('left')}
              style={{ 
                flex: 1,
                height: '32px',
                borderColor: textAlign === 'left' ? brandColor : styles.borderColor,
                color: textAlign === 'left' ? brandColor : styles.textSecondary,
                backgroundColor: 'transparent',
                borderRadius: 0,
                fontFamily: styles.bodyFont,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <AlignLeft size={16} />
              <span>Left</span>
            </Button>
            <Button
              onClick={() => setTextAlign('center')}
              style={{ 
                flex: 1,
                height: '32px',
                borderColor: textAlign === 'center' ? brandColor : styles.borderColor,
                color: textAlign === 'center' ? brandColor : styles.textSecondary,
                backgroundColor: 'transparent',
                borderRadius: 0,
                fontFamily: styles.bodyFont,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <AlignCenter size={16} />
              <span>Center</span>
            </Button>
            <Button
              onClick={() => setTextAlign('right')}
              style={{ 
                flex: 1,
                height: '32px',
                borderColor: textAlign === 'right' ? brandColor : styles.borderColor,
                color: textAlign === 'right' ? brandColor : styles.textSecondary,
                backgroundColor: 'transparent',
                borderRadius: 0,
                fontFamily: styles.bodyFont,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <AlignRight size={16} />
              <span>Right</span>
            </Button>
          </div>
        </div>

      </div>
    </Modal>
  );
};

const DashboardPage = () => {
  const { team } = useParams();
  
  
  // State management
  const [dashboard, setDashboard] = useState(null);
  const [dashboardId, setDashboardId] = useState(null);
  const [dashboardName, setDashboardName] = useState('');
  const [dashboardDescription, setDashboardDescription] = useState('');
  const [widgets, setWidgets] = useState([]);
  const [savedQueries, setSavedQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [publicUrl, setPublicUrl] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState('');
  const [newDashboardDescription, setNewDashboardDescription] = useState('');
  const [isCanvasMode, setIsCanvasMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [useSimpleLayout, setUseSimpleLayout] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  
  // Text widget state
  const [showTextModal, setShowTextModal] = useState(false);
  const [editingTextWidget, setEditingTextWidget] = useState(null);
  const [textWidgets, setTextWidgets] = useState([]);
  const [textPositions, setTextPositions] = useState({});
  const [textSizes, setTextSizes] = useState({});
  
  // Widget data state - store query results for each widget
  const [widgetData, setWidgetData] = useState({});
  const [executingWidgets, setExecutingWidgets] = useState(new Set());
  
  // Infinite canvas state
  const [widgetPositions, setWidgetPositions] = useState({});
  const [widgetSizes, setWidgetSizes] = useState({});
  const [draggedWidget, setDraggedWidget] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizingWidget, setResizingWidget] = useState(null);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [hoveredWidget, setHoveredWidget] = useState(null);
  const canvasRef = useRef(null);
  const canvasContainerRef = useRef(null);

  // Vertically expanding canvas: fixed width (container width), dynamic height
  const [canvasWidth, setCanvasWidth] = useState(1280);
  const [canvasHeight, setCanvasHeight] = useState(900);
  const BOTTOM_PADDING_PX = 48;

  // Fetch saved queries for adding widgets
  const fetchSavedQueries = useCallback(async () => {
    try {
      // Get team context from localStorage like other pages do
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      const teamId = selectedTeamData.id || selectedTeamData._id || localStorage.getItem('selectedTeam');
      
      if (!teamId) {
        console.error('No team selected for saved queries');
        return;
      }
      
      const response = await axiosInstance.get(`/query/saved/team/${teamId}`);
      setSavedQueries(response.data.queries || []);
    } catch (error) {
      console.error('Error fetching saved queries:', error);
      message.error('Failed to fetch saved queries');
    }
  }, []);


  // Execute query for a widget
  const executeWidgetQuery = useCallback(async (widgetId, savedQuery) => {
    if (!savedQuery) {
      console.error('No savedQuery found for widget:', widgetId);
      setWidgetData(prev => ({
        ...prev,
        [widgetId]: {
          error: 'Query not found or has been deleted',
          data: [],
          columns: [],
          config: {}
        }
      }));
      return;
    }
    
    if (!savedQuery.sqlQuery) {
      console.error('No SQL query found for widget:', widgetId);
      setWidgetData(prev => ({
        ...prev,
        [widgetId]: {
          error: 'No SQL query defined for this widget',
          data: [],
          columns: [],
          config: savedQuery.visualizationConfig || {}
        }
      }));
      return;
    }

    try {
      setExecutingWidgets(prev => new Set([...prev, widgetId]));
      
      // Get the currently selected team ID
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      const teamId = selectedTeamData._id;
      
      if (!teamId) {
        throw new Error('Team ID not found. Please select a team.');
      }
      
      const response = await axiosInstance.post('/query/execute', {
        query: savedQuery.sqlQuery,
        teamId: teamId // Include the currently selected team ID
      });
      
      // Handle batch execution results
      if (response.data.batchExecution) {
        // For batch queries, use the first successful query's data
        const firstQuery = response.data.queries?.find(q => q.success && q.data);
        if (firstQuery) {
          setWidgetData(prev => ({
            ...prev,
            [widgetId]: {
              data: firstQuery.data || [],
              columns: firstQuery.data?.length > 0 ? Object.keys(firstQuery.data[0]) : [],
              config: savedQuery.visualizationConfig || {}
            }
          }));
        } else {
          throw new Error('No successful queries in batch execution');
        }
      } else {
        setWidgetData(prev => ({
          ...prev,
          [widgetId]: {
            data: response.data.data || [],
            columns: response.data.columns || [],
            config: savedQuery.visualizationConfig || {}
          }
        }));
      }
      
    } catch (error) {
      console.error('Error executing query for widget:', widgetId, error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to execute query';
      setWidgetData(prev => ({
        ...prev,
        [widgetId]: {
          error: errorMessage,
          data: [],
          columns: [],
          config: savedQuery?.visualizationConfig || {}
        }
      }));
    } finally {
      setExecutingWidgets(prev => {
        const newSet = new Set(prev);
        newSet.delete(widgetId);
        return newSet;
      });
    }
  }, []);

  // Load dashboard if ID is provided in URL
  const loadDashboard = useCallback(async (id) => {
    try {
      setLoading(true);
      // Get team context from localStorage like other pages do
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      const teamId = selectedTeamData.id || selectedTeamData._id || localStorage.getItem('selectedTeam');
      
      if (!teamId) {
        console.error('No team selected');
        message.error('Please select a team first');
        setLoading(false);
        return;
      }
      
      const response = await axiosInstance.get(`/dashboards/team/${teamId}/${id}`);
      const dashboardData = response.data.data;
      
      setDashboard(dashboardData);
      setDashboardId(dashboardData._id);
      setDashboardName(dashboardData.name);
      setDashboardDescription(dashboardData.description || '');
      
      console.log('Loading dashboard - widgets from backend:', dashboardData.widgets);
      console.log('Widget count:', dashboardData.widgets?.length || 0);
      if (dashboardData.widgets && dashboardData.widgets.length > 0) {
        console.log('First widget details:', dashboardData.widgets[0]);
      }
      
      // Set widgets directly - they already have the full query data in savedQuery
      console.log('Setting widgets from backend:', dashboardData.widgets);
      setWidgets(dashboardData.widgets || []);
      setTextWidgets(dashboardData.textWidgets || []);
      
      // Extract positions and sizes from widgets
      const positions = {};
      const sizes = {};
      (dashboardData.widgets || []).forEach((widget) => {
        positions[widget.widgetId] = widget.position || { x: 0, y: 0 };
        sizes[widget.widgetId] = widget.size || { width: 600, height: 450 };
      });
      setWidgetPositions(positions);
      setWidgetSizes(sizes);
      
      // Extract positions and sizes from text widgets
      const textPos = {};
      const textSz = {};
      (dashboardData.textWidgets || []).forEach((textWidget) => {
        const textId = textWidget.textId;
        textPos[textId] = textWidget.position || { x: 0, y: 0 };
        textSz[textId] = textWidget.size || { width: 300, height: 100 };
      });
      setTextPositions(textPos);
      setTextSizes(textSz);
      
      // Apply saved canvas height if available
      if (dashboardData.settings?.totalCanvasHeight) {
        setCanvasHeight(dashboardData.settings.totalCanvasHeight);
      }

      setIsCanvasMode(true);
      setHasUnsavedChanges(false);
      
      // Execute queries for all widgets
      (dashboardData.widgets || []).forEach(widget => {
        // Only execute if savedQuery exists and has sqlQuery
        if (widget.savedQuery && widget.savedQuery.sqlQuery) {
          executeWidgetQuery(widget.widgetId, widget.savedQuery);
        } else if (!widget.savedQuery) {
          // Set error state for widgets with missing queries
          setWidgetData(prev => ({
            ...prev,
            [widget.widgetId]: {
              error: 'Query not found or has been deleted',
              data: [],
              columns: [],
              config: {}
            }
          }));
        }
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
      message.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);


  // Check if dashboard ID is in URL and determine view/edit mode
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id');
    const isEdit = urlParams.get('edit') === 'true';
    const isNew = urlParams.get('new') === 'true';
    setIsViewMode(!!id && !isEdit && !isNew);
    if (id) {
      loadDashboard(id);
    } else {
      // If no ID, start with a new dashboard
      setIsCanvasMode(true);
    }
  }, [loadDashboard]);

  // Fetch saved queries on component mount
  useEffect(() => {
    fetchSavedQueries();
  }, [fetchSavedQueries]);

  // Measure container width to set fixed canvas width = container width
  useEffect(() => {
    const measure = () => {
      if (canvasContainerRef.current) {
        const w = Math.floor(canvasContainerRef.current.clientWidth);
        if (w && w !== canvasWidth) setCanvasWidth(w);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [canvasWidth]);

  // Helper: clamp X within [0, canvasWidth - width]
  const clampXWithinCanvas = (x, width) => {
    const maxX = Math.max(0, canvasWidth - width);
    return Math.min(Math.max(0, x), maxX);
  };

  // Recompute canvas height based on bottom-most widget/text
  const recomputeCanvasHeight = useCallback(() => {
    let bottom = 0;
    Object.keys(widgetPositions).forEach((id) => {
      const pos = widgetPositions[id];
      const size = widgetSizes[id] || { width: 600, height: 450 };
      if (!pos) return;
      bottom = Math.max(bottom, (pos.y || 0) + (size.height || 0));
    });
    Object.keys(textPositions).forEach((id) => {
      const pos = textPositions[id];
      const size = textSizes[id] || { width: 300, height: 100 };
      if (!pos) return;
      bottom = Math.max(bottom, (pos.y || 0) + (size.height || 0));
    });
    const next = Math.max(600, bottom + BOTTOM_PADDING_PX);
    if (next !== canvasHeight) setCanvasHeight(next);
  }, [widgetPositions, widgetSizes, textPositions, textSizes, canvasHeight]);

  useEffect(() => {
    recomputeCanvasHeight();
  }, [recomputeCanvasHeight]);

  // Handle widget position change
  const handleWidgetPositionChange = (widgetId, position) => {
    setWidgetPositions(prev => ({
      ...prev,
      [widgetId]: position
    }));
    setHasUnsavedChanges(true);
  };

  // Zoom controls
  const handleZoomIn = (zoomIn) => {
    if (typeof zoomIn === 'function') {
      zoomIn();
    }
  };

  const handleZoomOut = (zoomOut) => {
    if (typeof zoomOut === 'function') {
      zoomOut();
    }
  };

  const handleResetZoom = (resetTransform) => {
    if (typeof resetTransform === 'function') {
      resetTransform();
    }
  };

  // Get center position of viewport for new widgets
  const getViewportCenter = () => {
    const width = canvasWidth || 1280;
    const height = canvasHeight || 900;
    return {
      x: clampXWithinCanvas(Math.floor(width / 2 - 300), 600),
      y: Math.max(0, Math.floor(height / 2 - 225))
    };
  };

  // Add widget to dashboard
  const addWidget = (savedQuery) => {
    console.log('addWidget called with savedQuery:', savedQuery);
    console.log('savedQuery._id:', savedQuery._id);
    console.log('savedQuery._id:', savedQuery._id);
    const widgetId = `widget_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Position new widget at center of viewport
    const centerPosition = getViewportCenter();
    const defaultSize = { width: 600, height: 450 };
    
    const newWidget = {
      widgetId,
      savedQuery: savedQuery._id, // Use the MongoDB _id for the saved query
      position: centerPosition,
      size: defaultSize,
      config: savedQuery.visualizationConfig || {},
      isVisible: true,
      savedQueryData: savedQuery // Store the full query data for rendering
    };
    
    console.log('New widget created:', newWidget);
    
    setWidgets(prev => {
      const updatedWidgets = [...prev, newWidget];
      console.log('After addWidget - updatedWidgets:', updatedWidgets);
      console.log('New widget savedQuery:', newWidget.savedQuery);
      console.log('New widget savedQueryData:', newWidget.savedQueryData);
      return updatedWidgets;
    });
    setWidgetPositions(prev => ({
      ...prev,
      [widgetId]: centerPosition
    }));
    setWidgetSizes(prev => ({
      ...prev,
      [widgetId]: defaultSize
    }));
    setTimeout(recomputeCanvasHeight, 0);
    setHasUnsavedChanges(true);
    setIsSidebarOpen(false);
    
    // Execute the query for the new widget
    executeWidgetQuery(widgetId, savedQuery);
  };

  // Add text widget to dashboard
  const addTextWidget = (textConfig) => {
    if (editingTextWidget) {
      // Update existing text widget
      setTextWidgets(prev => prev.map(tw => 
        tw.textId === editingTextWidget.textId
          ? {
              ...tw,
              text: textConfig.text,
              fontSize: textConfig.fontSize,
              color: textConfig.color,
              fontFamily: textConfig.fontFamily,
              fontWeight: textConfig.fontWeight,
              textAlign: textConfig.textAlign || 'left'
            }
          : tw
      ));
      setEditingTextWidget(null);
    } else {
      // Add new text widget
      const textId = `text_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Position new text widget at center of viewport
      const centerPosition = getViewportCenter();
      const defaultSize = { width: 300, height: 100 };
      
    const newTextWidget = {
      textId,
      text: textConfig.text,
      position: centerPosition,
      size: defaultSize,
      fontSize: textConfig.fontSize,
      color: textConfig.color,
      fontFamily: textConfig.fontFamily,
      fontWeight: textConfig.fontWeight,
      textAlign: textConfig.textAlign || 'left',
      isVisible: true
    };
      
      setTextWidgets(prev => [...prev, newTextWidget]);
      setTextPositions(prev => ({
        ...prev,
        [textId]: centerPosition
      }));
      setTextSizes(prev => ({
        ...prev,
        [textId]: defaultSize
      }));
      setTimeout(recomputeCanvasHeight, 0);
    }
    setHasUnsavedChanges(true);
    setShowTextModal(false);
  };

  // Edit text widget
  const editTextWidget = (textWidget) => {
    setEditingTextWidget(textWidget);
    setShowTextModal(true);
  };

  // Remove widget
  const removeWidget = (widgetId) => {
    setWidgets(prev => prev.filter(w => w.widgetId !== widgetId));
    setWidgetPositions(prev => {
      const newPositions = { ...prev };
      delete newPositions[widgetId];
      return newPositions;
    });
    setWidgetData(prev => {
      const newData = { ...prev };
      delete newData[widgetId];
      return newData;
    });
    setExecutingWidgets(prev => {
      const newSet = new Set(prev);
      newSet.delete(widgetId);
      return newSet;
    });
    setHasUnsavedChanges(true);
  };

  // Remove text widget
  const removeTextWidget = (textId) => {
    setTextWidgets(prev => prev.filter(t => t.textId !== textId));
    setTextPositions(prev => {
      const newPositions = { ...prev };
      delete newPositions[textId];
      return newPositions;
    });
    setTextSizes(prev => {
      const newSizes = { ...prev };
      delete newSizes[textId];
      return newSizes;
    });
    setHasUnsavedChanges(true);
  };

  // Widget drag handlers
  const handleWidgetMouseDown = (e, widgetId) => {
    if (isViewMode) return; // Disable dragging in view mode
    e.preventDefault();
    e.stopPropagation();
    
    const rect = e.currentTarget.getBoundingClientRect();
    const offset = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
    
    setDraggedWidget(widgetId);
    setDragOffset(offset);
    
    // Add dragging class for visual feedback
    e.currentTarget.classList.add('dragging');
  };

  // Resize handle mouse down handler
  const handleResizeMouseDown = (e, widgetId, handle) => {
    if (isViewMode) return; // Disable resizing in view mode
    e.preventDefault();
    e.stopPropagation();
    
    setResizingWidget(widgetId);
    setResizeHandle(handle);
  };

  const handleCanvasMouseMove = (e) => {
    if (isViewMode) return; // Disable interactions in view mode
    if (draggedWidget) {
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      if (!canvasRect) return;
      
      const isText = draggedWidget.startsWith('text_');
      const currentSize = isText 
        ? (textSizes[draggedWidget] || { width: 300, height: 100 })
        : (widgetSizes[draggedWidget] || { width: 600, height: 450 });

      let nextX = e.clientX - canvasRect.left - dragOffset.x;
      let nextY = e.clientY - canvasRect.top - dragOffset.y;
      nextX = clampXWithinCanvas(nextX, currentSize.width);
      const newPosition = { x: nextX, y: Math.max(0, nextY) };
      
      // Check if it's a text widget or regular widget
      if (draggedWidget.startsWith('text_')) {
        setTextPositions(prev => ({
          ...prev,
          [draggedWidget]: newPosition
        }));
      } else {
        setWidgetPositions(prev => ({
          ...prev,
          [draggedWidget]: newPosition
        }));
      }
      setHasUnsavedChanges(true);
      recomputeCanvasHeight();
    } else if (resizingWidget) {
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      if (!canvasRect) return;
      
      const isTextWidget = resizingWidget.startsWith('text_');
      const currentSize = isTextWidget 
        ? (textSizes[resizingWidget] || { width: 300, height: 100 })
        : (widgetSizes[resizingWidget] || { width: 600, height: 450 });
      const currentPosition = isTextWidget
        ? (textPositions[resizingWidget] || { x: 100, y: 100 })
        : (widgetPositions[resizingWidget] || { x: 100, y: 100 });
      
      const mouseX = e.clientX - canvasRect.left;
      const mouseY = e.clientY - canvasRect.top;
      
      let newSize = { ...currentSize };
      let newPosition = { ...currentPosition };
      
      const minWidth = isTextWidget ? 100 : 150;
      const minHeight = isTextWidget ? 50 : 120;
      
      if (resizeHandle.includes('e')) { // East (right)
        newSize.width = Math.max(minWidth, mouseX - currentPosition.x);
        newSize.width = Math.min(newSize.width, canvasWidth - currentPosition.x);
      }
      if (resizeHandle.includes('w')) { // West (left)
        const newWidth = Math.max(minWidth, currentSize.width - (mouseX - currentPosition.x));
        newSize.width = newWidth;
        newPosition.x = currentPosition.x + (currentSize.width - newWidth);
        newPosition.x = clampXWithinCanvas(newPosition.x, newSize.width);
      }
      if (resizeHandle.includes('s')) { // South (bottom)
        newSize.height = Math.max(minHeight, mouseY - currentPosition.y);
      }
      if (resizeHandle.includes('n')) { // North (top)
        const newHeight = Math.max(minHeight, currentSize.height - (mouseY - currentPosition.y));
        newSize.height = newHeight;
        newPosition.y = currentPosition.y + (currentSize.height - newHeight);
        newPosition.y = Math.max(0, newPosition.y);
      }
      
      if (isTextWidget) {
        setTextSizes(prev => ({
          ...prev,
          [resizingWidget]: newSize
        }));
        setTextPositions(prev => ({
          ...prev,
          [resizingWidget]: newPosition
        }));
      } else {
        setWidgetSizes(prev => ({
          ...prev,
          [resizingWidget]: newSize
        }));
        setWidgetPositions(prev => ({
          ...prev,
          [resizingWidget]: newPosition
        }));
      }
      setHasUnsavedChanges(true);
      recomputeCanvasHeight();
    }
  };

  const handleCanvasMouseUp = () => {
    if (isViewMode) return;
    if (draggedWidget) {
      // Remove dragging class
      const draggedElement = document.querySelector(
        draggedWidget.startsWith('text_') 
          ? `[data-text-id="${draggedWidget}"]` 
          : `[data-widget-id="${draggedWidget}"]`
      );
      if (draggedElement) {
        draggedElement.classList.remove('dragging');
      }
      
      setDraggedWidget(null);
      setDragOffset({ x: 0, y: 0 });
    } else if (resizingWidget) {
      setResizingWidget(null);
      setResizeHandle(null);
    }
  };


  // Handle share button click
  const handleShareClick = async () => {
    if (!dashboardId) {
      message.error('Please save the dashboard first');
      return;
    }
    
    setShareLoading(true);
    try {
      // Get the currently selected team ID
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      const teamId = selectedTeamData.id || selectedTeamData._id || localStorage.getItem('selectedTeam');
      
      if (!teamId) {
        message.error('Team ID not found. Please select a team.');
        setShareLoading(false);
        return;
      }
      
      // Check if dashboard already has public access
      const response = await axiosInstance.get(`/dashboards/${dashboardId}/share?teamId=${teamId}`);
      if (response.data.success && response.data.data.isPublic) {
        setPublicUrl(response.data.data.publicUrl);
        setShowShareModal(true);
      } else {
        // Generate new public URL
        const shareResponse = await axiosInstance.post(`/dashboards/${dashboardId}/share`, {
          teamId: teamId
        });
        if (shareResponse.data.success) {
          setPublicUrl(shareResponse.data.data.publicUrl);
          setShowShareModal(true);
        }
      }
    } catch (error) {
      console.error('Error getting share URL:', error);
      message.error('Failed to generate share URL');
    } finally {
      setShareLoading(false);
    }
  };

  // Copy public URL to clipboard
  const copyPublicUrl = async () => {
    if (!publicUrl) return;
    
    try {
      await navigator.clipboard.writeText(publicUrl);
      setUrlCopied(true);
      message.success('Public URL copied to clipboard!');
      setTimeout(() => setUrlCopied(false), 2000);
    } catch (error) {
      console.error('Error copying URL:', error);
      message.error('Failed to copy URL');
    }
  };

  // Revoke public access
  const revokePublicAccess = async () => {
    if (!dashboardId) return;
    
    setShareLoading(true);
    try {
      // Get the currently selected team ID
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      const teamId = selectedTeamData.id || selectedTeamData._id || localStorage.getItem('selectedTeam');
      
      if (!teamId) {
        message.error('Team ID not found. Please select a team.');
        setShareLoading(false);
        return;
      }
      
      await axiosInstance.delete(`/dashboards/${dashboardId}/share?teamId=${teamId}`);
      setPublicUrl(null);
      setShowShareModal(false);
      message.success('Public access revoked');
    } catch (error) {
      console.error('Error revoking access:', error);
      message.error('Failed to revoke public access');
    } finally {
      setShareLoading(false);
    }
  };

  // Save dashboard
  const saveDashboard = async () => {
    // Debug: Check if user is authenticated
    const token = localStorage.getItem("accessToken") || localStorage.getItem("token");
    console.log('Save Dashboard - Token check:', token ? 'Token exists' : 'No token found');
    console.log('Save Dashboard - User data:', localStorage.getItem("User"));
    console.log('Save Dashboard - Current widgets state:', widgets);
    console.log('Save Dashboard - Widgets length:', widgets.length);
    
    if (!dashboardId) {
      // Create new dashboard
      if (!newDashboardName.trim()) {
        message.error('Please enter a dashboard name');
        return;
      }
      
      try {
        setSaving(true);
        const dashboardData = {
          name: newDashboardName.trim(),
          description: newDashboardDescription.trim(),
          widgets: widgets
            .filter(widget => {
              // Extract query ID - handle both object and ID formats
              let queryId;
              if (typeof widget.savedQuery === 'object' && widget.savedQuery !== null) {
                // savedQuery is a full object, extract _id
                queryId = widget.savedQuery._id || widget.savedQuery.id;
              } else {
                // savedQuery is already an ID
                queryId = widget.savedQuery || widget.savedQueryData?._id;
              }
              // Only include widgets with valid query IDs (not undefined, null, NaN, or empty string)
              return queryId && queryId !== 'undefined' && queryId !== 'null' && queryId !== 'NaN' && String(queryId).trim() !== '';
            })
            .map(widget => {
              // Extract query ID - handle both object and ID formats
              let queryId;
              if (typeof widget.savedQuery === 'object' && widget.savedQuery !== null) {
                // savedQuery is a full object, extract _id
                queryId = widget.savedQuery._id || widget.savedQuery.id;
              } else {
                // savedQuery is already an ID
                queryId = widget.savedQuery || widget.savedQueryData?._id;
              }
              
              // Ensure queryId is a valid number or string
              const validQueryId = queryId && !isNaN(Number(queryId)) ? queryId : null;
              
              if (!validQueryId) {
                console.warn('Widget has invalid savedQuery ID:', widget.widgetId, queryId, 'savedQuery:', widget.savedQuery);
              }
              
              return {
                widgetId: widget.widgetId,
                savedQuery: validQueryId,
                position: widgetPositions[widget.widgetId] || { x: 0, y: 0 },
                size: widgetSizes[widget.widgetId] || { width: 600, height: 450 },
                config: widget.config,
                isVisible: widget.isVisible
              };
            })
            .filter(widget => widget.savedQuery !== null), // Remove widgets with invalid query IDs
          textWidgets: textWidgets.map(textWidget => {
            const textId = textWidget.id || textWidget.textId;
            return {
              textId: textId,
              text: textWidget.text,
              position: textPositions[textId] || { x: 0, y: 0 },
              size: textSizes[textId] || { width: 300, height: 100 },
              fontSize: textWidget.fontSize,
              color: textWidget.color,
              fontFamily: textWidget.fontFamily,
              fontWeight: textWidget.fontWeight,
              textAlign: textWidget.textAlign,
              isVisible: textWidget.isVisible
            };
          }),
          settings: {
            totalCanvasHeight: canvasHeight
          }
        };
        
        console.log('Creating dashboard with data:', JSON.stringify(dashboardData, null, 2));
        console.log('Widgets being sent:', dashboardData.widgets.length);
        console.log('Widget details:', dashboardData.widgets);
        
        // Get team context from localStorage like other pages do
        const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
        const teamId = selectedTeamData.id || selectedTeamData._id || localStorage.getItem('selectedTeam');
        
        if (!teamId) {
          console.error('No team selected for dashboard creation');
          message.error('Please select a team first');
          return;
        }
        
        const response = await axiosInstance.post(`/dashboards/team/${teamId}`, dashboardData);
        const newDashboard = response.data.data;
        
        if (!newDashboard || !newDashboard._id) {
          throw new Error('Dashboard creation failed: Invalid response from server');
        }
        
        setDashboard(newDashboard);
        setDashboardId(newDashboard._id);
        setDashboardName(newDashboard.name);
        setDashboardDescription(newDashboard.description || '');
        setShowSaveModal(false);
        setNewDashboardName('');
        setNewDashboardDescription('');
        setHasUnsavedChanges(false);
        
        // Update URL to include the new dashboard ID
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set('id', newDashboard._id);
        window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
        
        // Automatically generate public share token when dashboard is created
        try {
          const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
          const teamId = selectedTeamData.id || selectedTeamData._id || localStorage.getItem('selectedTeam');
          if (teamId) {
            await axiosInstance.post(`/dashboards/${newDashboard._id}/share`, {
              teamId: teamId
            });
          }
        } catch (shareError) {
          // Silently fail if share creation fails (dashboard is still created)
          console.warn('Failed to auto-create share link:', shareError);
        }
        
        message.success('Dashboard created successfully');
      } catch (error) {
        console.error('Error creating dashboard:', error);
        if (error?.response?.status === 409) {
          message.warning('A dashboard with this name already exists. Please choose a different name.');
          setShowSaveModal(true);
        } else {
          message.error('Failed to create dashboard');
        }
      } finally {
        setSaving(false);
      }
    } else {
      // Update existing dashboard
      try {
        setSaving(true);
        const dashboardData = {
          name: dashboardName,
          description: dashboardDescription,
          widgets: widgets
            .filter(widget => {
              // Extract query ID - handle both object and ID formats
              let queryId;
              if (typeof widget.savedQuery === 'object' && widget.savedQuery !== null) {
                // savedQuery is a full object, extract _id
                queryId = widget.savedQuery._id || widget.savedQuery.id;
              } else {
                // savedQuery is already an ID
                queryId = widget.savedQuery || widget.savedQueryData?._id;
              }
              // Only include widgets with valid query IDs (not undefined, null, NaN, or empty string)
              return queryId && queryId !== 'undefined' && queryId !== 'null' && queryId !== 'NaN' && String(queryId).trim() !== '';
            })
            .map(widget => {
              // Extract query ID - handle both object and ID formats
              let queryId;
              if (typeof widget.savedQuery === 'object' && widget.savedQuery !== null) {
                // savedQuery is a full object, extract _id
                queryId = widget.savedQuery._id || widget.savedQuery.id;
              } else {
                // savedQuery is already an ID
                queryId = widget.savedQuery || widget.savedQueryData?._id;
              }
              
              // Ensure queryId is a valid number or string
              const validQueryId = queryId && !isNaN(Number(queryId)) ? queryId : null;
              
              if (!validQueryId) {
                console.warn('Widget has invalid savedQuery ID:', widget.widgetId, queryId, 'savedQuery:', widget.savedQuery);
              }
              
              return {
                widgetId: widget.widgetId,
                savedQuery: validQueryId,
                position: widgetPositions[widget.widgetId] || { x: 0, y: 0 },
                size: widgetSizes[widget.widgetId] || { width: 600, height: 450 },
                config: widget.config,
                isVisible: widget.isVisible
              };
            })
            .filter(widget => widget.savedQuery !== null), // Remove widgets with invalid query IDs
          textWidgets: textWidgets.map(textWidget => {
            const textId = textWidget.id || textWidget.textId;
            return {
              textId: textId,
              text: textWidget.text,
              position: textPositions[textId] || { x: 0, y: 0 },
              size: textSizes[textId] || { width: 300, height: 100 },
              fontSize: textWidget.fontSize,
              color: textWidget.color,
              fontFamily: textWidget.fontFamily,
              fontWeight: textWidget.fontWeight,
              textAlign: textWidget.textAlign,
              isVisible: textWidget.isVisible
            };
          }),
          settings: {
            totalCanvasHeight: canvasHeight
          }
        };
        
        console.log('Dashboard data being sent:', JSON.stringify(dashboardData, null, 2));
        console.log('Dashboard ID:', dashboardId);
        console.log('Widgets data:', widgets);
        console.log('Widget positions:', widgetPositions);
        console.log('Widget sizes:', widgetSizes);
        
        // Debug: Check each widget's savedQuery property
        console.log('Total widgets count:', widgets.length);
        widgets.forEach((widget, index) => {
          console.log(`Widget ${index}:`, {
            widgetId: widget.widgetId,
            savedQuery: widget.savedQuery,
            savedQueryData: widget.savedQueryData,
            hasSavedQuery: !!widget.savedQuery,
            savedQueryType: typeof widget.savedQuery,
            fullWidget: widget
          });
        });
        
        // Get team context from localStorage like other pages do
        const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
        const teamId = selectedTeamData.id || selectedTeamData._id || localStorage.getItem('selectedTeam');
        
        if (!teamId) {
          console.error('No team selected for dashboard update');
          message.error('Please select a team first');
          return;
        }
        
        const response = await axiosInstance.put(`/dashboards/team/${teamId}/${dashboardId}`, dashboardData);
        
        // Update local state with response data to ensure sync
        if (response.data?.data) {
          const updatedDashboard = response.data.data;
          setDashboard(updatedDashboard);
          setDashboardName(updatedDashboard.name || dashboardName);
          setDashboardDescription(updatedDashboard.description || dashboardDescription);
        }
        
        setHasUnsavedChanges(false);
        
        // Automatically generate public share token when dashboard is saved
        try {
          const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
          const teamId = selectedTeamData.id || selectedTeamData._id || localStorage.getItem('selectedTeam');
          if (teamId) {
            await axiosInstance.post(`/dashboards/${dashboardId}/share`, {
              teamId: teamId
            });
          }
        } catch (shareError) {
          // Silently fail if share creation fails (dashboard is still saved)
          console.warn('Failed to auto-create share link:', shareError);
        }
        
        message.success('Dashboard saved successfully');
      } catch (error) {
        console.error('Error updating dashboard:', error);
        console.error('Error response data:', error.response?.data);
        console.error('Error response status:', error.response?.status);
        if (error?.response?.status === 409) {
          message.warning('A dashboard with this name already exists. Please choose a different name.');
          setShowSaveModal(true);
        } else {
          message.error('Failed to save dashboard');
        }
      } finally {
        setSaving(false);
      }
    }
  };

  // Start creating new dashboard
  const startNewDashboard = () => {
    setDashboard(null);
    setDashboardId(null);
    setDashboardName('');
    setDashboardDescription('');
    setWidgets([]);
    setTextWidgets([]);
    setWidgetPositions({});
    setTextPositions({});
    setWidgetSizes({});
    setTextSizes({});
    setIsCanvasMode(true);
    setHasUnsavedChanges(false);
  };

  // Render text widget
  const renderTextWidget = (textWidget) => {
    if (!textWidget.isVisible) return null;

    const position = textPositions[textWidget.textId] || { x: 100, y: 100 };
    const size = textSizes[textWidget.textId] || { width: 300, height: 100 };

    return (
      <div 
        key={textWidget.textId}
        data-text-id={textWidget.textId}
        className="canvas-widget"
        style={{ 
          position: 'absolute',
          left: position.x,
          top: position.y,
          width: `${size.width}px`,
          height: `${size.height}px`,
          display: 'flex', 
          alignItems: 'center',
          justifyContent: textWidget.textAlign,
          overflow: 'hidden',
          zIndex: (draggedWidget === textWidget.textId || resizingWidget === textWidget.textId) ? 1000 : 1,
          cursor: isViewMode ? 'default' : 'move',
          padding: '8px',
          backgroundColor: 'transparent',
          border: '1px dashed transparent',
          borderRadius: isViewMode ? 0 : '4px'
        }}
        onMouseDown={isViewMode ? undefined : (e) => handleWidgetMouseDown(e, textWidget.textId)}
        onMouseEnter={isViewMode ? undefined : () => setHoveredWidget(textWidget.textId)}
        onMouseLeave={isViewMode ? undefined : () => setHoveredWidget(null)}
      >
        <div
          style={{
            fontSize: textWidget.fontSize,
            color: textWidget.color,
            fontFamily: textWidget.fontFamily,
            fontWeight: textWidget.fontWeight,
            textAlign: textWidget.textAlign,
            width: '100%',
            wordWrap: 'break-word',
            lineHeight: 1.2
          }}
        >
          {textWidget.text}
        </div>
        
        {/* Controls overlay - only show on hover (hidden in view mode) */}
        {!isViewMode && hoveredWidget === textWidget.textId && (
          <div
            className="absolute top-0 right-0 flex gap-1 p-1 bg-white rounded shadow-lg"
            style={{ zIndex: 10 }}
          >
            <Tooltip title="Edit text">
              <Button
                type="text"
                size="small"
                icon={<Icon name="edit_pencil" size={14} color="currentColor" />}
                onClick={(e) => {
                  e.stopPropagation();
                  editTextWidget(textWidget);
                }}
                style={{ color: brandColor }}
              />
            </Tooltip>
            <Tooltip title="Remove text">
              <Button
                type="text"
                size="small"
                icon={<Icon name="delete" size={12} />}
                onClick={(e) => {
                  e.stopPropagation();
                  removeTextWidget(textWidget.textId);
                }}
                danger
              />
            </Tooltip>
          </div>
        )}
        
        {/* Resize handles - only show on hover (hidden in view mode) */}
        {!isViewMode && hoveredWidget === textWidget.textId && (
          <>
            {/* Blue border */}
            <div
              className="absolute inset-0 border-2 border-blue-500 border-dashed pointer-events-none"
              style={{ 
                top: -2, 
                left: -2, 
                right: -2, 
                bottom: -2,
                zIndex: 5
              }}
            />
            
            {/* Resize handles */}
            <div
              className="absolute top-0 left-0 w-full h-full pointer-events-none"
              style={{ zIndex: 10 }}
            >
              {/* Corner handles */}
              <div
                className="absolute w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-nw-resize pointer-events-auto"
                style={{ top: -6, left: -6 }}
                onMouseDown={(e) => handleResizeMouseDown(e, textWidget.textId, 'nw')}
              />
              <div
                className="absolute w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-ne-resize pointer-events-auto"
                style={{ top: -6, right: -6 }}
                onMouseDown={(e) => handleResizeMouseDown(e, textWidget.textId, 'ne')}
              />
              <div
                className="absolute w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-se-resize pointer-events-auto"
                style={{ bottom: -6, right: -6 }}
                onMouseDown={(e) => handleResizeMouseDown(e, textWidget.textId, 'se')}
              />
              <div
                className="absolute w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-sw-resize pointer-events-auto"
                style={{ bottom: -6, left: -6 }}
                onMouseDown={(e) => handleResizeMouseDown(e, textWidget.textId, 'sw')}
              />
              
              {/* Edge handles */}
              <div
                className="absolute w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-n-resize pointer-events-auto"
                style={{ top: -6, left: '50%', transform: 'translateX(-50%)' }}
                onMouseDown={(e) => handleResizeMouseDown(e, textWidget.textId, 'n')}
              />
              <div
                className="absolute w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-s-resize pointer-events-auto"
                style={{ bottom: -6, left: '50%', transform: 'translateX(-50%)' }}
                onMouseDown={(e) => handleResizeMouseDown(e, textWidget.textId, 's')}
              />
              <div
                className="absolute w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-w-resize pointer-events-auto"
                style={{ left: -6, top: '50%', transform: 'translateY(-50%)' }}
                onMouseDown={(e) => handleResizeMouseDown(e, textWidget.textId, 'w')}
              />
              <div
                className="absolute w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-e-resize pointer-events-auto"
                style={{ right: -6, top: '50%', transform: 'translateY(-50%)' }}
                onMouseDown={(e) => handleResizeMouseDown(e, textWidget.textId, 'e')}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  // Render widget
  const renderWidget = (widget) => {
    console.log('Rendering widget:', widget);
    if (!widget.isVisible) {
      console.log('Widget not visible:', widget.widgetId);
      return null;
    }
    
    // Use savedQueryData if available, otherwise fall back to savedQuery
    const savedQuery = widget.savedQueryData || widget.savedQuery;
    console.log('Widget savedQueryData:', widget.savedQueryData);
    console.log('Widget savedQuery:', widget.savedQuery);
    console.log('Using savedQuery for rendering:', savedQuery);
    
    // Handle missing or deleted query - show error widget instead of hiding
    if (!savedQuery) {
      console.warn('No savedQuery or savedQueryData for widget:', widget.widgetId);
      const position = widgetPositions[widget.widgetId] || { x: 100, y: 100 };
      const size = widgetSizes[widget.widgetId] || { width: 600, height: 450 };
      
      return (
        <div 
          key={widget.widgetId}
          data-widget-id={widget.widgetId}
          className="canvas-widget"
          style={{ 
            position: 'absolute',
            left: position.x,
            top: position.y,
            width: `${size.width}px`,
            height: `${size.height}px`,
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 1
          }}
        >
          <Card
            style={{ 
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: styles.cardBg,
              borderColor: '#ef4444',
              boxShadow: styles.cardShadow
            }}
            title={
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-red-500" style={{ 
                  fontFamily: styles.bodyFont,
                  fontWeight: 500
                }}>Query Not Found</span>
                {!isViewMode && (
                  <Tooltip title="Remove widget">
                    <Button
                      type="text"
                      size="small"
                      icon={<Icon name="delete" size={14} />}
                      onClick={() => removeWidget(widget.widgetId)}
                      danger
                      style={{ color: '#ef4444' }}
                    />
                  </Tooltip>
                )}
              </div>
            }
          >
            <div className="flex items-center justify-center h-full text-red-500">
              <div className="text-center">
                <div className="text-sm font-medium">Query Deleted or Not Found</div>
                <div className="text-xs mt-1 text-[#475569]">The query associated with this widget has been deleted or is no longer accessible.</div>
                {!isViewMode && (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => removeWidget(widget.widgetId)}
                    className="mt-2"
                    danger
                  >
                    Remove Widget
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </div>
      );
    }

    const widgetQueryData = widgetData[widget.widgetId];
    const isExecuting = executingWidgets.has(widget.widgetId);
    const position = widgetPositions[widget.widgetId] || { x: 100, y: 100 };
    const size = widgetSizes[widget.widgetId] || { width: 600, height: 450 };

    console.log('Widget position and size:', { position, size, widgetId: widget.widgetId });
    const widgetElement = (
      <div 
        key={widget.widgetId}
        data-widget-id={widget.widgetId}
        className="canvas-widget"
        style={{ 
          position: 'absolute',
          left: position.x,
          top: position.y,
          width: `${size.width}px`,
          height: `${size.height}px`,
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: (draggedWidget === widget.widgetId || resizingWidget === widget.widgetId) ? 1000 : 1,
          cursor: isViewMode ? 'default' : undefined
        }}
        onMouseDown={isViewMode ? undefined : (e) => handleWidgetMouseDown(e, widget.widgetId)}
        onMouseEnter={isViewMode ? undefined : () => setHoveredWidget(widget.widgetId)}
        onMouseLeave={isViewMode ? undefined : () => setHoveredWidget(null)}
      >
        <Card
          style={{ 
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: styles.cardBg,
            borderColor: styles.borderColor,
            boxShadow: styles.cardShadow,
            borderRadius: isViewMode ? 0 : undefined
          }}
          title={
            <div className="flex items-center justify-between w-full">
              <div className="flex flex-col min-w-0 flex-1">
                {(() => {
                  const isTable = savedQuery.visualizationType === 'Table';
                  const heading = isTable 
                    ? (widget.config?.tableName || '')
                    : (widget.config?.visualizationName || '');
                  const subheading = isTable
                    ? (widget.config?.tableDescription || '')
                    : (widget.config?.visualizationDescription || '');
                  
                  return (
                    <>
                      {heading && (
                        <span className="text-sm font-medium" style={{ 
                          color: styles.textPrimary,
                          fontFamily: styles.bodyFont,
                          fontWeight: 500
                        }}>{heading}</span>
                      )}
                      {subheading && (
                        <span className="text-xs mt-1" style={{ 
                          color: styles.textSecondary,
                          fontFamily: styles.bodyFont,
                          fontWeight: 400
                        }}>{subheading}</span>
                      )}
                    </>
                  );
                })()}
              </div>
              {!isViewMode && (
                <Space size="small" className="flex-shrink-0">
                  <Tooltip title="Remove widget">
                    <Button
                      type="text"
                      size="small"
                      icon={<Icon name="delete" size={14} />}
                      onClick={() => removeWidget(widget.widgetId)}
                      danger
                      style={{ color: '#ef4444' }}
                    />
                  </Tooltip>
                </Space>
              )}
            </div>
          }
          styles={{
            body: {
              flex: 1,
              padding: savedQuery.visualizationType === 'KPI' ? '0px' : '12px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            },
            header: {
              borderRadius: isViewMode ? 0 : undefined
            }
          }}
        >
          <div style={{ 
            flex: 1, 
            width: '100%', 
            height: '100%', 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {isExecuting ? (
              <div className="flex items-center justify-center h-full">
                <Spin size="large" />
              </div>
            ) : widgetQueryData?.error ? (
              <div className="flex items-center justify-center h-full text-red-500">
                <div className="text-center">
                  <div className="text-sm font-medium">Error</div>
                  <div className="text-xs mt-1">{widgetQueryData.error}</div>
                </div>
              </div>
            ) : widgetQueryData?.data ? (
              <div style={{ 
                flex: 1,
                width: '100%', 
                height: '100%', 
                overflow: savedQuery.visualizationType === 'KPI' ? 'hidden' : 'auto',
                display: 'flex',
                flexDirection: 'column',
                margin: savedQuery.visualizationType === 'KPI' ? '0' : undefined
              }}>
                <EnhancedVisualization
                  type={savedQuery.visualizationType}
                  data={widgetQueryData.data}
                  columns={widgetQueryData.columns}
                  config={widgetQueryData.config}
                  height={size.height - 80}
                  width={size.width}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-[#475569]">
                <div className="text-center">
                  <div className="text-sm">No data available</div>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => executeWidgetQuery(widget.widgetId, savedQuery)}
                    className="mt-2"
                  >
                    Load Data
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Card>
        
        {/* Resize handles - only show on hover (hidden in view mode) */}
        {!isViewMode && hoveredWidget === widget.widgetId && (
          <>
            {/* Blue border */}
            <div
              className="absolute inset-0 border-2 border-blue-500 border-dashed pointer-events-none"
              style={{ 
                top: -2, 
                left: -2, 
                right: -2, 
                bottom: -2,
                zIndex: 5
              }}
            />
            
            {/* Resize handles */}
            <div
              className="absolute top-0 left-0 w-full h-full pointer-events-none"
              style={{ zIndex: 10 }}
            >
              {/* Corner handles */}
              <div
                className="absolute w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-nw-resize pointer-events-auto"
                style={{ top: -6, left: -6 }}
                onMouseDown={(e) => handleResizeMouseDown(e, widget.widgetId, 'nw')}
              />
              <div
                className="absolute w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-ne-resize pointer-events-auto"
                style={{ top: -6, right: -6 }}
                onMouseDown={(e) => handleResizeMouseDown(e, widget.widgetId, 'ne')}
              />
              <div
                className="absolute w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-se-resize pointer-events-auto"
                style={{ bottom: -6, right: -6 }}
                onMouseDown={(e) => handleResizeMouseDown(e, widget.widgetId, 'se')}
              />
              <div
                className="absolute w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-sw-resize pointer-events-auto"
                style={{ bottom: -6, left: -6 }}
                onMouseDown={(e) => handleResizeMouseDown(e, widget.widgetId, 'sw')}
              />
              
              {/* Edge handles */}
              <div
                className="absolute w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-n-resize pointer-events-auto"
                style={{ top: -6, left: '50%', transform: 'translateX(-50%)' }}
                onMouseDown={(e) => handleResizeMouseDown(e, widget.widgetId, 'n')}
              />
              <div
                className="absolute w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-s-resize pointer-events-auto"
                style={{ bottom: -6, left: '50%', transform: 'translateX(-50%)' }}
                onMouseDown={(e) => handleResizeMouseDown(e, widget.widgetId, 's')}
              />
              <div
                className="absolute w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-w-resize pointer-events-auto"
                style={{ left: -6, top: '50%', transform: 'translateY(-50%)' }}
                onMouseDown={(e) => handleResizeMouseDown(e, widget.widgetId, 'w')}
              />
              <div
                className="absolute w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-e-resize pointer-events-auto"
                style={{ right: -6, top: '50%', transform: 'translateY(-50%)' }}
                onMouseDown={(e) => handleResizeMouseDown(e, widget.widgetId, 'e')}
              />
            </div>
          </>
        )}
      </div>
    );
    
    console.log('Widget element created for:', widget.widgetId, widgetElement);
    return widgetElement;
  };

  // Render welcome screen
  const renderWelcomeScreen = () => (
    <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
      <LayoutIcon size={64} style={{ color: styles.textSecondary }} className="mb-4" />
      <Title level={2} className="mb-2" style={{ 
        color: styles.textPrimary,
        fontFamily: styles.headingFont,
        fontWeight: 600
      }}>
        Custom Dashboard
      </Title>
      <Text className="mb-6 max-w-md" style={{ 
        color: styles.textSecondary,
        fontFamily: styles.bodyFont,
        fontWeight: 400
      }}>
        Create personalized dashboards by adding widgets from your saved queries. 
        Arrange them freely on the canvas and save when you're ready.
      </Text>
      <Button
        type="primary"
        size="large"
        icon={<Plus size={20} />}
        onClick={startNewDashboard}
        style={{ 
          backgroundColor: styles.textPrimary, 
          borderColor: styles.textPrimary,
          fontFamily: styles.bodyFont,
          fontWeight: 500
        }}
      >
        Create New Dashboard
      </Button>
    </div>
  );

  // Grid background component
  const GridBackground = ({ scale = 1 }) => {
    const gridSize = 20 * scale;
    const dotSize = 1 * scale;
    
    return (
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(circle, ${styles.borderColor} ${dotSize}px, transparent ${dotSize}px)
          `,
          backgroundSize: `${gridSize}px ${gridSize}px`,
          backgroundPosition: '0 0',
          pointerEvents: 'none',
          zIndex: 0
        }}
      />
    );
  };


  // Render canvas
  const renderCanvas = () => {
    return (
      <div className="h-full relative">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 min-w-0 gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <Title level={4} className="mb-0 truncate" style={{ 
              color: brandColor,
              fontFamily: styles.headingFont,
              fontWeight: 600
            }}>
              {dashboardName || 'Untitled Dashboard'}
            </Title>
            {!isViewMode && hasUnsavedChanges && (
              <Text type="secondary" className="text-sm whitespace-nowrap hidden sm:block" style={{ 
                color: styles.textSecondary,
                fontFamily: styles.bodyFont,
                fontWeight: 400
              }}>
                • Unsaved changes
              </Text>
            )}
          </div>
          {!isViewMode && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              icon={<Plus size={14} />}
              onClick={() => setIsSidebarOpen(true)}
              style={{ 
                color: brandColor,
                borderColor: brandColor,
                backgroundColor: styles.primaryBackground,
                fontFamily: styles.bodyFont,
                fontWeight: 500,
                fontSize: '13px',
                borderRadius: 0,
                height: '32px',
                padding: '6px 16px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <span className="hidden sm:inline">Add Widget</span>
              <span className="sm:hidden">Add</span>
            </Button>
            <Button
              icon={<Type size={14} />}
              onClick={() => setShowTextModal(true)}
              style={{ 
                color: brandColor,
                borderColor: brandColor,
                backgroundColor: styles.primaryBackground,
                fontFamily: styles.bodyFont,
                fontWeight: 500,
                fontSize: '13px',
                borderRadius: 0,
                height: '32px',
                padding: '6px 16px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <span className="hidden sm:inline">Add Text</span>
              <span className="sm:hidden">Text</span>
            </Button>
            <Button
              type="primary"
              icon={<Save size={14} />}
              onClick={() => setShowSaveModal(true)}
              loading={saving}
              style={{ 
                backgroundColor: brandColor, 
                borderColor: brandColor,
                color: '#ffffff',
                fontFamily: styles.bodyFont,
                fontWeight: 500,
                fontSize: '13px',
                borderRadius: 0,
                height: '32px',
                padding: '6px 16px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <span className="hidden sm:inline">{dashboardId ? 'Save' : 'Save Dashboard'}</span>
              <span className="sm:hidden">{dashboardId ? 'Save' : 'Save'}</span>
            </Button>
          </div>
          )}
        </div>
        
        <div 
          className={`${isViewMode ? 'relative overflow-hidden canvas-background view-mode' : 'border border-solid relative overflow-hidden canvas-background'}`}
          ref={canvasContainerRef}
          style={{ 
            backgroundColor: styles.cardBg,
            borderColor: isViewMode ? 'transparent' : styles.borderColor,
            borderWidth: isViewMode ? 0 : undefined,
            boxShadow: 'none',
            overflowX: 'hidden',
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 100px)',
            borderRadius: 0
          }}
        >
          <div
            ref={canvasRef}
            style={{
              width: `${canvasWidth}px`,
              height: `${canvasHeight}px`,
              position: 'relative',
              margin: '0 auto'
            }}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={handleCanvasMouseUp}
            onMouseLeave={handleCanvasMouseUp}
          >
            {!isViewMode && <GridBackground scale={1} />}
            {(widgets.length > 0 || textWidgets.length > 0) && (
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                {widgets.map(widget => renderWidget(widget))}
                {textWidgets.map(textWidget => renderTextWidget(textWidget))}
              </div>
            )}
            {widgets.length === 0 && textWidgets.length === 0 && (
              <div 
                className="absolute inset-0 flex items-center justify-center z-10"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(2px)'
                }}
              >
                <div className="text-center p-8 border-2 border-dashed" style={{
                  backgroundColor: styles.cardBg,
                  borderColor: styles.borderColor,
                  maxWidth: '400px',
                  boxShadow: 'none',
                  borderRadius: 0
                }}>
                  <Title level={4} style={{ 
                    color: '#1d0c46',
                    fontFamily: styles.headingFont,
                    fontWeight: 500,
                    marginBottom: '8px'
                  }}>
                    No widgets yet
                  </Title>
                  <Text style={{ 
                    color: '#1d0c46',
                    fontFamily: styles.bodyFont,
                    fontSize: '14px'
                  }}>
                    Click "Add Widget" to start building your dashboard
                  </Text>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden relative" style={{ 
      fontFamily: styles.bodyFont,
      backgroundColor: styles.primaryBackground 
    }}>
      {/* Sidebar */}
      <div className="h-screen sticky top-0 flex-shrink-0">
        <Sidebar 
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          currentPage="custom-dashboard"
        />
      </div>

      {/* Main Content Area */}
      <div className="flex flex-col flex-1 h-screen relative z-10 min-w-0">
        <div className="flex-1 px-6 pt-6 overflow-auto min-w-0">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Spin size="large" />
            </div>
          ) : (
            !isCanvasMode ? renderWelcomeScreen() : renderCanvas()
          )}
        </div>
      </div>

      {/* Saved Queries Sidebar */}
      <Modal
        title={
          <span style={{ 
            color: '#1d0c46',
            fontFamily: styles.headingFont,
            fontWeight: 600,
            fontSize: '16px'
          }}>
            Add Widget
          </span>
        }
        open={isSidebarOpen}
        onCancel={() => setIsSidebarOpen(false)}
        footer={null}
        width={480}
        centered
        styles={{
          content: {
            borderRadius: 0,
            padding: 0,
            backgroundColor: styles.primaryBackground
          },
          header: {
            borderRadius: 0,
            borderBottom: `1px solid ${styles.borderColor}`,
            padding: '16px 24px',
            margin: 0,
            backgroundColor: styles.primaryBackground
          },
          body: {
            padding: '20px 24px',
            overflow: 'hidden',
            backgroundColor: styles.primaryBackground,
            maxHeight: 'calc(100vh - 200px)',
            minHeight: '400px'
          }
        }}
        maskStyle={{
          backgroundColor: 'rgba(0, 0, 0, 0.45)'
        }}
      >
        <SavedQueriesSidebar
          savedQueries={savedQueries}
          onLoadQuery={addWidget}
          onRefresh={fetchSavedQueries}
          loading={loading}
        />
      </Modal>

      {/* Share Dashboard Modal */}
      <Modal
        title={
          <span style={{ 
            color: '#1d0c46',
            fontFamily: styles.headingFont,
            fontWeight: 600
          }}>
            Share Dashboard
          </span>
        }
        open={showShareModal}
        onCancel={() => {
          setShowShareModal(false);
          setUrlCopied(false);
        }}
        footer={[
          publicUrl ? (
            <Button
              key="revoke"
              danger
              onClick={revokePublicAccess}
              loading={shareLoading}
              style={{ 
                fontFamily: styles.bodyFont,
                fontWeight: 500,
                borderRadius: 0
              }}
            >
              Revoke Access
            </Button>
          ) : null,
          <Button
            key="close"
            onClick={() => {
              setShowShareModal(false);
              setUrlCopied(false);
            }}
            style={{ 
              fontFamily: styles.bodyFont,
              fontWeight: 500,
              borderRadius: 0
            }}
          >
            Close
          </Button>
        ]}
        style={{ 
          fontFamily: styles.bodyFont 
        }}
        styles={{
          content: {
            borderRadius: 0,
            padding: 0
          },
          header: {
            borderRadius: 0,
            borderBottom: '1px solid #E5E7EB',
            padding: '16px 24px',
            margin: 0
          },
          body: {
            padding: '20px 24px',
            borderRadius: 0
          },
          footer: {
            borderRadius: 0,
            borderTop: '1px solid #E5E7EB',
            padding: '12px 24px'
          }
        }}
      >
        <div style={{ padding: '16px 0' }}>
          <Text style={{ 
            color: styles.textSecondary,
            fontFamily: styles.bodyFont,
            fontSize: '14px',
            display: 'block',
            marginBottom: '16px'
          }}>
            Anyone with this link can view your dashboard. You can revoke access at any time.
          </Text>
          
          {publicUrl ? (
            <div style={{ 
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              padding: '12px',
              backgroundColor: styles.secondaryBackground,
              borderRadius: '4px',
              border: `1px solid ${styles.borderColor}`
            }}>
              <Input
                value={publicUrl}
                readOnly
                style={{
                  fontFamily: styles.bodyFont,
                  fontSize: '13px',
                  flex: 1
                }}
              />
              <Button
                icon={urlCopied ? <Check size={16} /> : <Copy size={16} />}
                onClick={copyPublicUrl}
                style={{
                  fontFamily: styles.bodyFont,
                  fontWeight: 500,
                  borderColor: brandColor,
                  color: brandColor
                }}
              >
                {urlCopied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '24px' }}>
              <Spin size="large" />
            </div>
          )}
        </div>
      </Modal>

      {/* Save Dashboard Modal */}
      <Modal
        title={
          <span style={{ 
            color: '#1d0c46',
            fontFamily: styles.headingFont,
            fontWeight: 600
          }}>
            {dashboardId ? "Save Dashboard" : "Create Dashboard"}
          </span>
        }
        open={showSaveModal}
        onCancel={() => setShowSaveModal(false)}
        onOk={saveDashboard}
        confirmLoading={saving}
        okText={dashboardId ? "Save" : "Create"}
        width={500}
        style={{ top: 20 }}
        styles={{
          content: {
            borderRadius: 0,
            padding: 0
          },
          header: {
            borderRadius: 0,
            borderBottom: '1px solid #E5E7EB',
            padding: '16px 24px',
            margin: 0
          },
          body: {
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
            padding: '20px',
            backgroundColor: styles.primaryBackground,
            borderRadius: 0
          },
          footer: {
            borderRadius: 0,
            borderTop: '1px solid #E5E7EB',
            padding: '12px 24px'
          }
        }}
        okButtonProps={{
          className: "transition-all duration-200 hover:opacity-90",
          style: {
            backgroundColor: '#1d0c46',
            borderColor: '#1d0c46',
            border: 'none',
            color: '#ffffff',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 500,
            fontSize: '13px',
            borderRadius: '0px',
            padding: '6px 12px',
            height: '28px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px'
          }
        }}
        cancelButtonProps={{
          className: "transition-all duration-200 hover:opacity-90",
          style: {
            color: '#0f172a',
            borderColor: '#E5E7EB',
            borderWidth: '1px',
            borderStyle: 'solid',
            backgroundColor: '#ffffff',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 500,
            fontSize: '13px',
            borderRadius: '0px',
            padding: '6px 12px',
            height: '28px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px'
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ 
              color: styles.textPrimary,
              fontFamily: styles.headingFont,
              fontWeight: 500
            }}>
              Dashboard Name
            </label>
            <Input
              value={dashboardId ? dashboardName : newDashboardName}
              onChange={(e) => dashboardId ? setDashboardName(e.target.value) : setNewDashboardName(e.target.value)}
              placeholder="Enter dashboard name"
              style={{ 
                fontFamily: styles.bodyFont,
                borderColor: styles.borderColor,
                borderRadius: '8px'
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{ 
              color: styles.textPrimary,
              fontFamily: styles.headingFont,
              fontWeight: 500
            }}>
              Description (Optional)
            </label>
            <Input.TextArea
              value={dashboardId ? dashboardDescription : newDashboardDescription}
              onChange={(e) => dashboardId ? setDashboardDescription(e.target.value) : setNewDashboardDescription(e.target.value)}
              placeholder="Enter dashboard description"
              rows={3}
              style={{ 
                fontFamily: styles.bodyFont,
                borderColor: styles.borderColor,
                borderRadius: '8px'
              }}
            />
          </div>
        </div>
      </Modal>

      {/* Add Text Modal */}
      <AddTextModal
        open={showTextModal}
        onCancel={() => {
          setShowTextModal(false);
          setEditingTextWidget(null);
        }}
        onAdd={addTextWidget}
        initialValues={editingTextWidget ? {
          text: editingTextWidget.text,
          fontSize: editingTextWidget.fontSize,
          color: editingTextWidget.color,
          fontFamily: editingTextWidget.fontFamily,
          fontWeight: editingTextWidget.fontWeight,
          textAlign: editingTextWidget.textAlign
        } : null}
        isEdit={!!editingTextWidget}
      />
    </div>
  );
};

export default DashboardPage;
